/**
 * Calm Command — 30 wellness-flavoured Missile Command levels across 6 neon biomes.
 *
 * The player defends 6 "Anchor" bases at the bottom of the screen from incoming
 * stress waves by launching arcing calm blasts. Any stress wave that touches an
 * expanding blast is neutralized. Anchors take 1 hit before being lost.
 *
 * Star rules (uniform):
 *   ⭐   Clear the wave (survive the incoming missiles queue).
 *   ⭐⭐  Clear with 3 or more anchors remaining.
 *   ⭐⭐⭐ Clear with all 6 anchors remaining.
 */

export const BIOMES = [
  {
    id: 1, name: 'Neon Dawn', range: [1, 5],
    bg: ['#0a0f2a', '#1b1a4d'],
    accent: '#7dfaff', glow: '#ff5cff',
    scene: 'city',
    tagline: 'Ease into the guardhouse. Small waves, plenty of light.',
  },
  {
    id: 2, name: 'Aurora Forest', range: [6, 10],
    bg: ['#052a24', '#0d4d47'],
    accent: '#39ffcf', glow: '#8affa1',
    scene: 'forest',
    tagline: 'The green line hums. Learn the arcs before they land.',
  },
  {
    id: 3, name: 'Solar Desert', range: [11, 15],
    bg: ['#3a1a06', '#7a3a10'],
    accent: '#ffcf3a', glow: '#ffb43a',
    scene: 'desert',
    tagline: 'Heat rises. Prioritize what closes fastest.',
  },
  {
    id: 4, name: 'Twilight Coast', range: [16, 20],
    bg: ['#1a0530', '#4a0d70'],
    accent: '#c17dff', glow: '#7d3aff',
    scene: 'coast',
    tagline: 'Softer sky. Faster incoming. Trust your timing.',
  },
  {
    id: 5, name: 'Rose Mountains', range: [21, 25],
    bg: ['#2a0410', '#6b0a24'],
    accent: '#ff3a7d', glow: '#ff7d3a',
    scene: 'mountains',
    tagline: 'Waves split as they fall. Read the fork.',
  },
  {
    id: 6, name: 'Prism Frontier', range: [26, 30],
    bg: ['#04162a', '#0a2f6b'],
    accent: '#7dfaff', glow: '#ffe23a',
    scene: 'prism',
    tagline: 'Everything at once. Six colors, one steady hand.',
  },
];

export function biomeForLevel(level) {
  return BIOMES.find(b => level >= b.range[0] && level <= b.range[1]) || BIOMES[0];
}

/**
 * The 6 Anchor pillars — displayed left-to-right at the bottom of the field.
 * Each represents a mental-wellness anchor. Order stays fixed across all
 * levels so the player builds spatial familiarity.
 */
export const ANCHORS = [
  { id: 'family',    label: 'Family',    color: '#ff5cff' },
  { id: 'body',      label: 'Body',      color: '#39ffcf' },
  { id: 'mind',      label: 'Mind',      color: '#7dfaff' },
  { id: 'purpose',   label: 'Purpose',   color: '#ffcf3a' },
  { id: 'rest',      label: 'Rest',      color: '#c17dff' },
  { id: 'joy',       label: 'Joy',       color: '#ff3a7d' },
];

const NAMES = [
  'First Watch',    'Steady Post',    'Hold the Light',  'Read the Arc',    'Six Anchors',
  'Green Guard',    'Twin Streaks',   'Emerald Line',    'Vector Focus',    'Aurora Master',
  'Amber Rise',     'Solar Volley',   'Split-Second',    'Coronal Wall',    'The Bright Line',
  'Violet Whisper', 'Fork Reader',    'Silent Storm',    'Twilight Threshold','The Deep Purple',
  'Rose Dawn',      'Split Waves',    'Blood Line',      'The Divide',      'Crimson Command',
  'Prism Awakens',  'Refract Path',   'Spectrum Guard',  'Peace Bearer',    'The Final Prism',
];

const INTROS = [
  'Six anchors. Slow waves. Tap where you want the calm to bloom.',
  'Feel the pace. Bloom the calm ahead of the wave, not on it.',
  'Read the arc. Aim early. Aim wide.',
  'Fewer taps. Better ones.',
  'You know the six. Now guard them.',
  'The green line hums. Trust it.',
  'Two streaks at once. Split your gaze, not your calm.',
  'Emerald pace. Feel the drop.',
  'Aim between, not at.',
  'You are the aurora master.',
  'Amber rises. Read the fastest first.',
  'Volley incoming. Bloom wide.',
  'Half a second matters. Feel the middle.',
  'A coronal wall of calm. Hold it.',
  'The bright line is peace. Cross it steady.',
  'Silent night. Faster incoming.',
  'Waves fork. Anticipate the second half.',
  'A storm you already know how to survive.',
  'Almost through. Same steady breath.',
  'The deep purple ends here.',
  'A crimson dawn to guard.',
  'Every wave splits. Every calm covers two.',
  'Not chaos. Just density.',
  'The divide is where mastery lives.',
  'Take command. It was always yours.',
  'A gift of a wave. Feel the colors.',
  'Refract every threat into a bloom.',
  'Spectrum weaving — your best breathing.',
  'You are the peace this field needs.',
  'Bring it home. Master run.',
];

/**
 * Level factory — deterministic difficulty ramp.
 * Each level is a fixed-duration survival challenge. Missiles keep raining
 * for the full duration; the player wins by keeping at least 1 anchor alive
 * until the timer runs out.
 * @param {number} n level 1..30
 */
function buildLevel(n) {
  const b = biomeForLevel(n);
  const inBiomeT = (n - b.range[0]) / (b.range[1] - b.range[0] || 1);
  const overallT = (n - 1) / 29;
  const lerp = (a, x, t) => a + (x - a) * t;

  // Duration to survive (seconds). Grows through the campaign.
  //   Biome 1: 25 → 35s      Biome 4: 60 → 75s
  //   Biome 2: 35 → 45s      Biome 5: 75 → 90s
  //   Biome 3: 45 → 60s      Biome 6: 90 → 120s
  const duration = [
    Math.round(lerp(25, 35,  inBiomeT)),
    Math.round(lerp(35, 45,  inBiomeT)),
    Math.round(lerp(45, 60,  inBiomeT)),
    Math.round(lerp(60, 75,  inBiomeT)),
    Math.round(lerp(75, 90,  inBiomeT)),
    Math.round(lerp(90, 120, inBiomeT)),
  ][b.id - 1];

  // Missile fall speed (px/sec at 60fps baseline).
  const missileSpeed = lerp(46, 100, overallT);

  // Time between missile spawns (seconds). Shrinks with level.
  const spawnEvery = lerp(1.6, 0.55, overallT);

  // Splitter missiles start showing from biome 5. Fraction of missiles that split.
  const splitterFrac = b.id >= 5 ? lerp(0.15, 0.35, inBiomeT) : 0;

  // Blast radius (max) — pretty forgiving on early levels
  const blastRadius = lerp(78, 58, overallT);

  // Interceptor cooldown between launches
  const launchCooldown = lerp(0.28, 0.22, overallT);

  const isBoss = (n % 5 === 0);

  return {
    id: n,
    biome: b.id,
    name: NAMES[n - 1] || `Level ${n}`,
    intro: INTROS[n - 1] || 'Steady.',
    duration,
    missileSpeed,
    spawnEvery,
    splitterFrac,
    blastRadius,
    launchCooldown,
    isBoss,
    starThresholds: {
      one: 'survive',
      two: 'survive + 3 anchors alive',
      three: 'survive + all 6 anchors alive',
    },
  };
}

export const LEVELS = Array.from({ length: 30 }, (_, i) => buildLevel(i + 1));
export const MAX_LEVEL = 30;

/**
 * Star computation for a completed Calm Command run.
 * @param {object} level  the level config
 * @param {object} run    { cleared:bool, anchorsLeft:number }
 * @returns 0 | 1 | 2 | 3
 */
export function computeStars(level, run) {
  if (!run.cleared) return 0;
  const anchors = run.anchorsLeft ?? 0;
  if (anchors >= 6) return 3;   // Flawless — all anchors alive
  if (anchors >= 3) return 2;
  return 1;
}
